import ContrastWarning from '@/components/form/ThemeBuilder/ContrastWarning'
import { generateSmartTheme } from '@/lib/storefront-theme'
import ThemePreview from '@/components/forms/DesignForm/ThemePreview'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import withSuspense from '@/hooks/withSuspense'
import { useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import { useStoreThemes } from '@/lib/services/stores/stores.query'
import { colorSchema } from '@/lib/services/stores/stores.schema'
import { convertToNewThemeColors } from '@/lib/utils'
import { Palette, Wrench } from 'lucide-react'
import { Fragment, useCallback, useEffect } from 'react'
import { toast } from 'sonner'

const PREFIX = 'brand'
const name = (str) => `${PREFIX}.${str}`

// List of available colors, grouped by bg/text color combos
const themeColors = [
  [
    { name: 'primary', label: 'Primary', description: 'Primary brand color' },
    {
      name: 'primary_foreground',
      label: 'Primary Foreground',
      description: 'Text color on primary elements',
    },
  ],
  [
    { name: 'background', label: 'Background', description: 'Main background color' },
    { name: 'foreground', label: 'Foreground', description: 'Main text color' },
  ],
  [
    { name: 'secondary', label: 'Secondary', description: 'Secondary brand color' },
    {
      name: 'secondary_foreground',
      label: 'Secondary Foreground',
      description: 'Text color on secondary elements',
    },
  ],
  [
    { name: 'accent', label: 'Accent', description: 'Accent brand color' },
    {
      name: 'accent_foreground',
      label: 'Accent Foreground',
      description: 'Text color on accent elements',
    },
  ],
]

const PresetSection = () => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const { data: themes } = useStoreThemes()

  const handleCustomize = (presetId) => {
    const preset = themes?.find((theme) => +theme.id === +presetId)
    if (!preset) {
      toast.error('Error customizing theme', {
        description: 'Please refresh the page and try again.',
      })
      return
    }
    const colors = convertToNewThemeColors(preset?.properties)
    form.setFieldValue(PREFIX, (prev) => ({ ...prev, ...colors, type: 'manual' }))
  }

  return (
    <div className="flex flex-col gap-y-4">
      <Typography variant="supporting">
        Start with a ready-made color palette and customize it if needed.
      </Typography>
      <form.AppField name={name('preset_theme_id')}>
        {(field) => (
          <div>
            <field.SelectInput options={themes} />
            <Button
              size="sm"
              disabled={isReadOnly}
              className="mt-3"
              onClick={() => handleCustomize(field.state.value)}
            >
              <Wrench /> Customize This Palette
            </Button>
          </div>
        )}
      </form.AppField>
    </div>
  )
}

const AutoSection = () => {
  const form = useFormContext()

  const handleUpdateTheme = useCallback(() => {
    if (!colorSchema.safeParse(form.getFieldValue(name('primary')))?.success) return

    const color = form.getFieldValue(name('primary'))
    const mode = form.getFieldValue(name('theme_mode'))
    const accent = form.getFieldValue(name('accent_style'))

    const theme = generateSmartTheme(color, mode, accent)
    if (theme?.colors) {
      const colors = convertToNewThemeColors(theme?.colors)
      form.setFieldValue(PREFIX, (prev) => ({ ...prev, ...colors, type: 'auto' }))
    }
  }, [form])

  useEffect(() => {
    // Update theme colors when switching to this tab
    handleUpdateTheme()
  }, [handleUpdateTheme])

  return (
    <div className="w-full @container">
      <Typography variant="supporting" className="mb-6 block">
        Quick start: choose one brand color and we will generate a matching palette.
      </Typography>
      <div className="grid grid-cols-1 gap-4 @xl:grid-cols-3">
        <form.AppField
          name={name('primary')}
          validators={{ onChange: colorSchema }}
          listeners={{
            onChange: handleUpdateTheme,
          }}
        >
          {(field) => <field.ColorInput label="Brand Color" />}
        </form.AppField>
        <form.AppField
          name={name('theme_mode')}
          listeners={{
            onChange: handleUpdateTheme,
          }}
        >
          {(field) => (
            <field.SelectInput
              label="Color Mood"
              options={[
                { id: 'soft', name: 'Soft' },
                { id: 'light', name: 'Light' },
                { id: 'dark', name: 'Dark' },
              ]}
            />
          )}
        </form.AppField>

        <form.AppField
          name={name('accent_style')}
          listeners={{
            onChange: handleUpdateTheme,
          }}
        >
          {(field) => (
            <field.SelectInput
              label="Accent Pairing"
              options={[
                { id: 'standard', name: 'Standard' },
                { id: 'complementary', name: 'Complementary' },
              ]}
            />
          )}
        </form.AppField>
      </div>
    </div>
  )
}

const ManualSection = () => {
  const form = useFormContext()
  return (
    <>
      <Typography variant="supporting" className="mb-6 block">
        Customize each color manually for full control.
      </Typography>
      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
        {themeColors.map(([bg, fg]) => (
          <Fragment key={bg.name}>
            <form.AppField name={name(bg.name)} validators={{ onChange: colorSchema }}>
              {(field) => <field.ColorInput label={bg.label} description={bg.description} />}
            </form.AppField>
            <form.AppField name={name(fg.name)} validators={{ onChange: colorSchema }}>
              {(field) => <field.ColorInput label={fg.label} description={fg.description} />}
            </form.AppField>
            <form.Subscribe
              selector={(state) => [state.values[PREFIX][bg.name], state.values[PREFIX][fg.name]]}
            >
              {([bgColor, fgColor]) => (
                <div className="@3xl:col-span-2 w-full">
                  <ContrastWarning
                    backgroundName={bg.label}
                    foregroundName={fg.label}
                    background={bgColor}
                    foreground={fgColor}
                    onApply={(newColor) => form.setFieldValue(name(fg.name), newColor)}
                    checkMutedContrast={bg.name === 'background' && fg.name === 'foreground'}
                  />
                </div>
              )}
            </form.Subscribe>
          </Fragment>
        ))}
      </div>
    </>
  )
}

const ThemeForm = ({ showPreview = true }) => {
  const form = useFormContext()
  const { data: themes } = useStoreThemes()
  const { isReadOnly } = useIsReadOnly()

  return (
    <div className="@container w-full">
      <div className={`grid grid-cols-1 gap-6 ${showPreview ? '@4xl:grid-cols-2 @4xl:gap-8' : ''}`}>
        <div className={`flex w-full flex-col gap-y-3 ${showPreview ? '@4xl:max-w-lg' : ''}`}>
          <div>
            <Typography variant="h4" className="flex items-center gap-2 mb-2">
              <Palette className="size-5" /> Theme Colors
            </Typography>
            <div>
              <Typography variant="supporting">
                Pick colors that fit your shop style. Choose one of these options:
              </Typography>
            </div>
          </div>
          <div className="grid gap-y-4">
            <form.AppField name={name('type')}>
              {(field) => (
                <div>
                  <Tabs value={field.state.value} onValueChange={field.handleChange} size="compact">
                    <TabsList className="mb-4 gap-1" layout="fill">
                      <TabsTrigger disabled={isReadOnly} value="preset">
                        Ready-Made
                      </TabsTrigger>
                      <TabsTrigger disabled={isReadOnly} value="auto">
                        Quick Start
                      </TabsTrigger>
                      <TabsTrigger disabled={isReadOnly} value="manual">
                        Custom
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="preset">
                      <PresetSection />
                    </TabsContent>
                    <TabsContent value="auto">
                      <AutoSection />
                    </TabsContent>
                    <TabsContent value="manual">
                      <ManualSection />
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </form.AppField>
          </div>
        </div>
        {showPreview ? (
          <form.Subscribe selector={(state) => state.values.brand}>
            {(brand) => {
              const presetColors =
                brand?.type === 'preset' &&
                themes?.find((t) => +t.id === +brand?.preset_theme_id)?.properties

              return <ThemePreview colors={presetColors || brand} />
            }}
          </form.Subscribe>
        ) : null}
      </div>
    </div>
  )
}

export default withSuspense(ThemeForm)
